/**
 * FS-11-04 관찰 메모·성취 — 화면 밖 규칙(시험하기 쉽게 분리).
 *  - 정렬은 이름순 고정(성취·AGS 로 줄 세우지 않는다 — 성취 열은 입력값이지 비교 지표가 아니다)
 *  - 오늘 활동 요약(교사 참고용 — 코스 완주/미완주 · 밀어내기 · 관람 · 협동) — 와이어 '코스 보통 완주 · 스모 1회' 꼴
 *  - 성취 칩 · AGS 칩(전송됨 · 대기 · 미입력 · 실패 · 재시도 중 · 실패)
 */
import type { Translate } from "../../../i18n";
import type { AgsState, AssessStudent, TodayStats, ChipTone, Draft } from "../types";

/** 이름순(한국어 정렬) — 같은 이름이면 pid 로 고정 */
export function byName(xs: readonly AssessStudent[]): AssessStudent[] {
  return [...xs].sort(
    (a, b) => a.nickname.localeCompare(b.nickname, "ko") || a.pid.localeCompare(b.pid),
  );
}

/** 오늘 활동 조각들(없으면 빈 배열 → '—') — 순서: 코스 · 밀어내기 · 관람 · 협동 */
export function todayParts(t: Translate, d: TodayStats): string[] {
  const out: string[] = [];
  if (d.finishes > 0)
    out.push(
      d.finishes === 1 ? t("assess.today.finish") : t("assess.today.finishN", { n: d.finishes }),
    );
  else if (d.tries > 0) out.push(t("assess.today.notFinished", { n: d.tries }));
  if (d.pushout > 0) out.push(t("assess.today.pushout", { n: d.pushout }));
  if (d.visits > 0) out.push(t("assess.today.visits", { n: d.visits }));
  if (d.carry > 0)
    out.push(d.carry === 1 ? t("assess.today.carry") : t("assess.today.carryN", { n: d.carry }));
  return out;
}

export const todayText = (t: Translate, d: TodayStats) => todayParts(t, d).join(" · ") || "—";

/** 성취 칩 — 확정값(초록) · 자동 제안만(노랑 '2 (자동 제안)') · 없음(회색 —) */
export function levelChip(t: Translate, s: Pick<AssessStudent, "level" | "suggested">) {
  if (s.level !== null) return { tone: "ok" as ChipTone, text: String(s.level) };
  if (s.suggested !== null)
    return { tone: "run" as ChipTone, text: t("assess.suggestedChip", { n: s.suggested }) };
  return { tone: "off" as ChipTone, text: "—" };
}

/**
 * AGS 칩 — 서버 상태 그대로. 'none' 인데 자동 제안이 있으면 '확정 대기'(제안은 교사가 저장해야 AGS 로 간다 — SR-INT-03).
 */
export function agsChip(t: Translate, ags: AgsState, suggested: 2 | null) {
  switch (ags) {
    case "sent":
      return { tone: "ok" as ChipTone, text: t("assess.ags.sent") };
    case "pending":
      return { tone: "run" as ChipTone, text: t("assess.ags.pending") };
    case "retrying":
      return { tone: "err" as ChipTone, text: t("assess.ags.retrying") };
    case "failed":
      return { tone: "err" as ChipTone, text: t("assess.ags.failed") };
    default:
      return suggested !== null
        ? { tone: "run" as ChipTone, text: t("assess.ags.confirm") }
        : { tone: "off" as ChipTone, text: t("assess.ags.none") };
  }
}

/** 이전/다음 학생 pid(끝이면 null) */
export function neighbour(
  rows: readonly AssessStudent[],
  pid: string | null,
  dir: -1 | 1,
): string | null {
  const i = rows.findIndex((r) => r.pid === pid);
  if (i < 0) return null;
  return rows[i + dir]?.pid ?? null;
}

export const draftOf = (s: AssessStudent): Draft => ({ note: s.note, level: s.level });

export const isDirty = (s: AssessStudent, d: Draft | undefined) =>
  !!d && (d.note !== s.note || d.level !== s.level);
